import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Search, Tag, Eye, Truck, Archive, Home, Printer, FileText, Trash2,
  CheckCircle, XCircle, Plus, Network, Package as PackageIcon,
  Link2, Link2Off, Copy as CopyIcon, ExternalLink,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { openLetterheadWindow } from '../utils/brandedPrint';
import { toast } from 'sonner';
import { csvCell } from '../lib/utils';
import WorkOrderLabelDialog from '../components/WorkOrderLabelDialog';

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || (typeof window !== 'undefined' ? window.location.origin : '');

const FinishedUnitsPage = () => {
  const { api, user } = useAuth();
  const [units, setUnits] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('onsite');
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('all');

  // Detail
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [noteBody, setNoteBody] = useState('');
  const [bols, setBols] = useState([]);

  // Ship dialog
  const [shipOpen, setShipOpen] = useState(false);
  const [shipForm, setShipForm] = useState({ shipped_to: '', tracking: '', note: '' });

  // iter78q — Print label dialog
  const [labelOpen, setLabelOpen] = useState(false);
  const [labelUnit, setLabelUnit] = useState(null);
  const openLabel = (unit) => { setLabelUnit(unit); setLabelOpen(true); };

  const fetchUnits = async () => {
    setLoading(true);
    try {
      const [onsiteRes, shippedRes, ptRes] = await Promise.all([
        api().get('/finished-units?lifecycle_state=onsite'),
        api().get('/finished-units?lifecycle_state=shipped'),
        api().get('/product-types'),
      ]);
      setUnits([...onsiteRes.data, ...shippedRes.data]);
      setProductTypes(ptRes.data);
    } catch {
      toast.error('Failed to load finished units');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUnits(); }, []);

  const ptMap = useMemo(() => productTypes.reduce((acc, p) => { acc[p.id] = p; return acc; }, {}), [productTypes]);

  const filtered = useMemo(() => {
    const inLifecycle = units.filter(u => (u.lifecycle_state || (u.current_status === 'Shipped' ? 'shipped' : 'onsite')) === activeTab);
    return inLifecycle.filter(u => {
      const pt = ptMap[u.product_type_id];
      if (kindFilter !== 'all' && (u.unit_kind || 'part_assembly') !== kindFilter) return false;
      const q = search.toLowerCase();
      if (!q) return true;
      return (u.serial_number || '').toLowerCase().includes(q) ||
             (pt?.product_type_name || '').toLowerCase().includes(q);
    });
  }, [units, activeTab, search, kindFilter, ptMap]);

  const counts = useMemo(() => {
    const onsite = units.filter(u => (u.lifecycle_state || (u.current_status === 'Shipped' ? 'shipped' : 'onsite')) === 'onsite');
    const shipped = units.filter(u => (u.lifecycle_state || (u.current_status === 'Shipped' ? 'shipped' : 'onsite')) === 'shipped');
    return { onsite: onsite.length, shipped: shipped.length };
  }, [units]);

  const openDetail = async (unit) => {
    setDetail(null);
    setBols([]);
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const [detailRes, bolsRes] = await Promise.all([
        api().get(`/finished-units/${unit.id}/full-detail`),
        api().get(`/bills-of-lading?finished_unit_id=${unit.id}`).catch(() => ({ data: [] })),
      ]);
      setDetail(detailRes.data);
      setBols(bolsRes.data);
    } catch {
      toast.error('Failed to load details');
    } finally {
      setDetailLoading(false);
    }
  };

  const addNote = async () => {
    if (!detail || !noteBody.trim()) return;
    try {
      await api().post(`/finished-units/${detail.unit.id}/notes`, { body: noteBody });
      setNoteBody('');
      const res = await api().get(`/finished-units/${detail.unit.id}/full-detail`);
      setDetail(res.data);
      toast.success('Note added');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  // iter37 — Linked BOMs management state
  const [linkBomsOpen, setLinkBomsOpen] = useState(false);
  const [allBoms, setAllBoms] = useState([]);
  const [linkedDraft, setLinkedDraft] = useState([]);

  const openLinkBoms = async () => {
    if (!detail) return;
    try {
      const res = await api().get('/boms');
      setAllBoms((res.data || []).filter(b => b.id !== detail.unit.bom_id));
      setLinkedDraft(detail.unit.linked_bom_ids || []);
      setLinkBomsOpen(true);
    } catch (e) { toast.error('Failed to load BOMs'); }
  };
  const saveLinkedBoms = async () => {
    try {
      await api().put(`/finished-units/${detail.unit.id}/linked-boms`, { linked_bom_ids: linkedDraft });
      const res = await api().get(`/finished-units/${detail.unit.id}/full-detail`);
      setDetail(res.data);
      setLinkBomsOpen(false);
      toast.success(`${linkedDraft.length} BOM(s) linked`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // iter37 — public link issue/revoke
  const issuePublicLink = async () => {
    try {
      const res = await api().post(`/finished-units/${detail.unit.id}/public-link`);
      const d2 = await api().get(`/finished-units/${detail.unit.id}/full-detail`);
      setDetail(d2.data);
      toast.success(`Public link created: token ending …${res.data.token.slice(-6)}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const revokePublicLink = async () => {
    if (!window.confirm('Revoke this public link? Any printed reports referencing the QR will stop working.')) return;
    try {
      await api().delete(`/finished-units/${detail.unit.id}/public-link`);
      const d2 = await api().get(`/finished-units/${detail.unit.id}/full-detail`);
      setDetail(d2.data);
      toast.success('Public link revoked');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // iter37 — Final Assembly Report (printable + CSV)
  const openFinalReport = async (mode = 'print') => {
    if (!detail) return;
    try {
      const res = await api().get(`/finished-units/${detail.unit.id}/full-report`);
      const r = res.data;
      const escape = (s) => String(s ?? '').replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
      const fmt = (s) => s ? new Date(s).toLocaleString() : '—';
      const publicUrl = r.unit.id && detail.unit.public_token && !detail.unit.public_token_revoked
        ? `${FRONTEND_URL}/public/finished-units/${detail.unit.public_token}`
        : '';
      // CSV mode
      if (mode === 'csv') {
        const lines = [];
        lines.push(['Final Assembly Report', r.unit.serial_number].map(csvCell).join(','));
        lines.push('Generated,' + new Date(r.generated_at).toLocaleString());
        lines.push('');
        lines.push('Component (rolled up across all linked BOMs)');
        lines.push('SKU,Name,UOM,Total Qty,From BOMs');
        r.components_rolled_up.forEach(c => lines.push([c.component_sku, (c.component_name || '').replace(/,/g, ' '), c.uom || '', c.quantity_total, (c.from_boms || []).map(x => x.bom_name).join(' | ')].map(csvCell).join(',')));
        lines.push('');
        lines.push('QC Submissions');
        lines.push('Date,Template,Status,Submitted By');
        r.qc_submissions.forEach(q => lines.push([q.submitted_at || '', (q.template_name || '').replace(/,/g, ' '), q.overall_status || '', q.submitted_by_name || ''].map(csvCell).join(',')));
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob); const a = document.createElement('a');
        a.href = url; a.download = `final-assembly-${r.unit.serial_number}.csv`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        return;
      }
      // Generate QR for the PDF embed (public link if exists, otherwise serial)
      const qrPayload = publicUrl || `SERIAL:${r.unit.serial_number}`;
      // QRCodeSVG cannot be rendered server-side; use Google Charts API as a stable image source
      const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(qrPayload)}`;
      const html = `<!doctype html><html><head><title>Final Assembly Report — ${escape(r.unit.serial_number)}</title>
        <style>
          @page { size: Letter; margin: 0.55in; }
          body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #111; line-height: 1.4; }
          h1 { font-size: 20px; margin: 0; letter-spacing: -0.02em; }
          h2 { font-size: 11px; margin: 16px 0 6px; text-transform: uppercase; letter-spacing: 0.12em; color: #475569; border-bottom: 1px solid #cbd5e1; padding-bottom: 4px; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #0f172a; padding-bottom: 10px; gap: 14px; }
          .header .logo { max-height: 50px; max-width: 180px; object-fit: contain; }
          .qr-box { text-align: center; }
          .qr-box img { width: 110px; height: 110px; border: 1px solid #e2e8f0; padding: 4px; background: white; }
          .qr-box .lbl { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 0.12em; margin-top: 4px; }
          .qr-box .url { font-size: 8px; color: #94a3b8; word-break: break-all; margin-top: 2px; max-width: 130px; }
          .meta { text-align: right; font-size: 11px; color: #666; }
          .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin: 14px 0; }
          .kpi { border: 1px solid #e2e8f0; padding: 8px; border-radius: 4px; background: #f8fafc; text-align: center; }
          .kpi .v { font-size: 18px; font-weight: 800; color: #0f172a; }
          .kpi .l { font-size: 8px; color: #64748b; text-transform: uppercase; letter-spacing: 0.1em; margin-top: 2px; }
          table { width: 100%; border-collapse: collapse; margin: 4px 0 12px; }
          th, td { padding: 4px 6px; border-bottom: 1px solid #e5e7eb; text-align: left; font-size: 10px; }
          th { background: #f1f5f9; font-weight: 600; color: #334155; text-transform: uppercase; letter-spacing: 0.06em; font-size: 8.5px; }
          .right { text-align: right; }
          .pill { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 8px; }
          .footer { margin-top: 22px; font-size: 9px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 6px; }
          .recall { background: #fee2e2; border-left: 3px solid #b91c1c; padding: 6px 10px; font-size: 11px; color: #991b1b; margin: 8px 0; }
        </style></head><body>
        <div class="header">
          <div style="flex:1"><!--LH--></div>
          <div class="qr-box">
            <img src="${qrImg}" />
            <div class="lbl">${publicUrl ? 'Scan for live online card' : 'Serial QR'}</div>
            ${publicUrl ? `<div class="url">${publicUrl}</div>` : ''}
          </div>
          <div class="meta">
            <div><strong>Final Assembly</strong></div>
            <div style="font-family:monospace;">${escape(r.unit.serial_number)}</div>
            <div>${escape((r.product_type || {}).product_type_name || r.unit.unit_kind)}</div>
            <div>Built ${fmt(r.unit.build_date)} by ${escape(r.unit.built_by_name || '')}</div>
            <div>Generated ${fmt(r.generated_at)}</div>
          </div>
        </div>

        ${r.unit.has_active_recall ? `<div class="recall">⚠ This unit has an active recall flag. See "Active Recalls" section below.</div>` : ''}

        <div class="kpis">
          <div class="kpi"><div class="v">${r.totals.boms}</div><div class="l">BOMs Linked (incl. primary)</div></div>
          <div class="kpi"><div class="v">${r.totals.rolled_up_skus}</div><div class="l">Distinct SKUs</div></div>
          <div class="kpi"><div class="v">${r.totals.qc_pass}/${r.totals.qc}</div><div class="l">QC Pass / Total</div></div>
          <div class="kpi"><div class="v">${r.totals.linked_units}</div><div class="l">Linked Sub-Assemblies</div></div>
        </div>

        <h2>Linked BOMs</h2>
        <table>
          <tr><th>BOM</th><th>Variant</th><th>Rev</th><th>Role</th><th class="right">Lines</th></tr>
          ${(r.boms_grouped || []).map(b => `<tr><td>${escape(b.bom_name || b.bom_id)}</td><td>${escape(b.bom_variant_code || '')}</td><td>${escape(b.revision_number || '')}</td><td>${b.is_primary ? '<span class="pill" style="background:#0f172a;color:white;">Primary</span>' : '<span class="pill" style="background:#e2e8f0;color:#334155;">Linked PA</span>'}</td><td class="right">${(b.lines || []).length}</td></tr>`).join('')}
        </table>

        <h2>Components — Rolled Up Across All BOMs</h2>
        ${r.components_rolled_up.length === 0 ? '<div style="color:#94a3b8;font-size:10px;">No components.</div>' : `
        <table>
          <tr><th>SKU</th><th>Name</th><th>UOM</th><th class="right">Total Qty</th><th>From BOM(s)</th></tr>
          ${r.components_rolled_up.map(c => `<tr><td style="font-family:monospace;">${escape(c.component_sku)}</td><td>${escape(c.component_name || '')}</td><td>${escape(c.uom || '')}</td><td class="right">${c.quantity_total}</td><td style="font-size:8.5px;color:#64748b;">${(c.from_boms || []).map(b => escape(b.bom_name || '')).filter(Boolean).join(', ')}</td></tr>`).join('')}
        </table>`}

        <h2>Components by BOM</h2>
        ${(r.boms_grouped || []).map(b => `
          <div style="font-size:11px;font-weight:600;margin:8px 0 2px;">${escape(b.bom_name || '')} ${b.is_primary ? '· primary' : '· linked'}</div>
          <table>
            <tr><th>SKU</th><th>Name</th><th>UOM</th><th class="right">Qty/Unit</th></tr>
            ${(b.lines || []).map(ln => `<tr><td style="font-family:monospace;">${escape(ln.component_sku)}</td><td>${escape(ln.component_name || '')}</td><td>${escape(ln.uom || '')}</td><td class="right">${ln.quantity_per_unit}</td></tr>`).join('')}
          </table>`).join('')}

        <h2>QC Submissions (${r.qc_submissions.length})</h2>
        ${r.qc_submissions.length === 0 ? '<div style="color:#94a3b8;font-size:10px;">No QC submissions yet.</div>' : `
        <table>
          <tr><th>When</th><th>Template</th><th>Status</th><th>Submitted By</th></tr>
          ${r.qc_submissions.map(q => `<tr><td>${fmt(q.submitted_at)}</td><td>${escape(q.template_name || '')}</td><td>${(q.overall_status || '').toUpperCase()}</td><td>${escape(q.submitted_by_name || '')}</td></tr>`).join('')}
        </table>`}

        ${(r.linked_units || []).length > 0 ? `
        <h2>Linked Sub-Assemblies</h2>
        <table><tr><th>Serial</th><th>Built</th><th>Built By</th><th>Lifecycle</th></tr>
        ${r.linked_units.map(u => `<tr><td style="font-family:monospace;">${escape(u.serial_number)}</td><td>${fmt(u.build_date)}</td><td>${escape(u.built_by_name || '')}</td><td>${escape(u.lifecycle_state || '')}</td></tr>`).join('')}
        </table>` : ''}

        ${(r.bols || []).length > 0 ? `
        <h2>Shipping</h2>
        <table><tr><th>BoL #</th><th>Issued</th><th>Consignee</th><th>PRO/Tracking</th></tr>
        ${r.bols.map(b => `<tr><td>${escape(b.bol_number)}</td><td>${fmt(b.issued_at)}</td><td>${escape(b.consignee_name || '')}</td><td>${escape(b.pro_number || b.tracking_number || '')}</td></tr>`).join('')}
        </table>` : ''}

        ${(r.active_recalls || []).length > 0 ? `
        <h2 style="color:#991b1b;">⚠ Active Recalls</h2>
        <table><tr><th>Lot</th><th>Reason</th><th>Issued</th></tr>
        ${r.active_recalls.map(x => `<tr><td style="font-family:monospace;">${escape(x.lot)}</td><td>${escape(x.reason || '')}</td><td>${fmt(x.created_at)}</td></tr>`).join('')}
        </table>` : ''}

        <script>window.onload = () => setTimeout(() => window.print(), 600);</script>
        </body></html>`;
      const w = await openLetterheadWindow(html, { features: 'width=1000,height=1200' });
      if (!w) { toast.error('Pop-ups blocked. Allow pop-ups to print the report.'); return; }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    }
  };

  const deleteNote = async (noteId) => {
    if (!detail) return;
    if (!window.confirm('Delete this note?')) return;
    try {
      await api().delete(`/finished-units/${detail.unit.id}/notes/${noteId}`);
      const res = await api().get(`/finished-units/${detail.unit.id}/full-detail`);
      setDetail(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const openShip = (unit) => {
    setDetail((d) => d || { unit });
    setShipForm({ shipped_to: '', tracking: '', note: '' });
    setShipOpen(true);
  };

  const submitShip = async () => {
    if (!detail) return;
    try {
      await api().post(`/finished-units/${detail.unit.id}/ship`, shipForm);
      toast.success('Unit shipped → archived');
      setShipOpen(false);
      setDetailOpen(false);
      fetchUnits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const returnOnsite = async () => {
    if (!detail) return;
    if (!window.confirm('Return this unit to Onsite? (used for recall)')) return;
    try {
      await api().post(`/finished-units/${detail.unit.id}/return-onsite`);
      toast.success('Unit returned onsite');
      setDetailOpen(false);
      fetchUnits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const printDetail = async () => {
    if (!detail) return;
    const html = renderUnitSheetHtml(detail);
    const w = await openLetterheadWindow(html, { title: 'Finished Unit', subtitle: detail.unit?.serial_number || '' });
    if (w) setTimeout(() => w.print(), 400);
  };

  return (
    <div className="space-y-4 animate-slideIn" data-testid="finished-units-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Finished Units</h1>
          <p className="text-slate-500 text-sm">Each completed unit as a product card. Onsite units are visible on the dashboard; Shipped units are archived for genealogy &amp; recall.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-600" data-testid="onsite-count-badge">{counts.onsite} onsite</Badge>
            <Badge className="bg-slate-600" data-testid="shipped-count-badge">{counts.shipped} shipped</Badge>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by serial or product…" className="pl-9 rounded-sm" data-testid="finished-units-search" />
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant={kindFilter === 'all' ? 'default' : 'outline'} onClick={() => setKindFilter('all')} data-testid="kind-filter-all">All</Button>
          <Button size="sm" variant={kindFilter === 'final_product' ? 'default' : 'outline'} onClick={() => setKindFilter('final_product')} data-testid="kind-filter-final"><PackageIcon className="w-3 h-3 mr-1" /> Final</Button>
          <Button size="sm" variant={kindFilter === 'part_assembly' ? 'default' : 'outline'} onClick={() => setKindFilter('part_assembly')} data-testid="kind-filter-part"><Network className="w-3 h-3 mr-1" /> Part</Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="onsite" data-testid="tab-onsite"><Home className="w-4 h-4 mr-2" /> Onsite ({counts.onsite})</TabsTrigger>
          <TabsTrigger value="shipped" data-testid="tab-shipped"><Archive className="w-4 h-4 mr-2" /> Shipped Archive ({counts.shipped})</TabsTrigger>
        </TabsList>

        <TabsContent value="onsite" className="mt-4">
          <UnitGrid loading={loading} items={filtered} ptMap={ptMap} onOpen={openDetail} onShip={openShip} onPrintLabel={openLabel} testidPrefix="onsite" />
        </TabsContent>
        <TabsContent value="shipped" className="mt-4">
          <UnitGrid loading={loading} items={filtered} ptMap={ptMap} onOpen={openDetail} onPrintLabel={openLabel} archive testidPrefix="shipped" />
        </TabsContent>
      </Tabs>

      {/* iter78q — Print Label dialog (label-size picker + remember per device) */}
      <WorkOrderLabelDialog
        open={labelOpen}
        onOpenChange={setLabelOpen}
        wo={labelUnit ? { id: labelUnit.work_order_id || labelUnit.id } : null}
        unit={labelUnit}
        bom={null}
        pt={labelUnit ? ptMap[labelUnit.product_type_id] : null}
      />

      {/* DETAIL DIALOG (CRM card) */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-4 h-4" /> Finished Unit Detail
            </DialogTitle>
          </DialogHeader>
          {detailLoading || !detail ? (
            <div className="h-48 flex items-center justify-center"><div className="spinner" /></div>
          ) : (
            <UnitDetailBody detail={detail} ptMap={ptMap} noteBody={noteBody} setNoteBody={setNoteBody} addNote={addNote} deleteNote={deleteNote} currentUser={user} bols={bols}
              onIssuePublicLink={issuePublicLink} onRevokePublicLink={revokePublicLink} onOpenLinkBoms={openLinkBoms} />
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
            {detail?.unit && (
              <Button onClick={printDetail} variant="outline" data-testid="unit-detail-print"><Printer className="w-4 h-4 mr-1" /> Print Card</Button>
            )}
            {detail?.unit && detail.unit.unit_kind === 'final_product' && (
              <>
                <Button onClick={() => openFinalReport('print')} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="final-report-print-btn">
                  <FileText className="w-4 h-4 mr-1" /> Final Assembly Report
                </Button>
                <Button onClick={() => openFinalReport('csv')} variant="outline" data-testid="final-report-csv-btn">CSV</Button>
                <Button onClick={openLinkBoms} variant="outline" data-testid="final-link-boms-btn">
                  <Network className="w-4 h-4 mr-1" /> Linked BOMs ({(detail.unit.linked_bom_ids || []).length})
                </Button>
                {!detail.unit.public_token || detail.unit.public_token_revoked ? (
                  <Button onClick={issuePublicLink} variant="outline" data-testid="final-public-issue-btn">Issue Public Link</Button>
                ) : (
                  <Button onClick={revokePublicLink} variant="outline" className="text-red-600" data-testid="final-public-revoke-btn">Revoke Public Link</Button>
                )}
              </>
            )}
            {detail?.unit && (detail.unit.lifecycle_state || 'onsite') === 'onsite' && (
              <Button onClick={() => openShip(detail.unit)} className="bg-blue-600 hover:bg-blue-700" data-testid="unit-detail-ship-btn">
                <Truck className="w-4 h-4 mr-1" /> Mark Shipped
              </Button>
            )}
            {detail?.unit && (detail.unit.lifecycle_state === 'shipped' || detail.unit.current_status === 'Shipped') && (
              <Button onClick={returnOnsite} variant="outline" data-testid="unit-detail-return-btn">
                <Home className="w-4 h-4 mr-1" /> Return Onsite
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SHIP DIALOG */}
      <Dialog open={shipOpen} onOpenChange={setShipOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Ship Unit · move to Archive</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p className="text-slate-500">Unit Serial: <span className="font-mono font-bold">{detail?.unit?.serial_number}</span></p>
            <div className="space-y-2">
              <Label>Shipped To</Label>
              <Input value={shipForm.shipped_to} onChange={e => setShipForm({ ...shipForm, shipped_to: e.target.value })} placeholder="Customer or destination" data-testid="ship-shipped-to-input" />
            </div>
            <div className="space-y-2">
              <Label>Tracking #</Label>
              <Input value={shipForm.tracking} onChange={e => setShipForm({ ...shipForm, tracking: e.target.value })} placeholder="Carrier tracking" data-testid="ship-tracking-input" />
            </div>
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Textarea value={shipForm.note} onChange={e => setShipForm({ ...shipForm, note: e.target.value })} rows={2} placeholder="e.g., shipped via FedEx ground" />
            </div>
            <p className="text-xs text-slate-500">After shipping, the unit moves to the Shipped Archive tab and is no longer counted on the dashboard. It remains queryable for Unit Genealogy and recall traces.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShipOpen(false)}>Cancel</Button>
            <Button onClick={submitShip} className="bg-blue-600 hover:bg-blue-700" data-testid="ship-submit-btn">
              <Truck className="w-4 h-4 mr-1" /> Confirm Ship
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter37 — LINKED BOMs Dialog (Final Products) */}
      <Dialog open={linkBomsOpen} onOpenChange={setLinkBomsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Network className="w-4 h-4" /> Linked Part-Assembly BOMs
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p className="text-slate-500 text-xs">
              The Final Assembly Card consolidates parts from the primary BOM and any additional Part-Assembly BOMs you link here.
              {' '}The primary BOM is always included automatically and cannot be linked twice.
            </p>
            {detail?.bom && (
              <div className="border border-slate-200 rounded-sm p-2 bg-slate-50">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Primary BOM (always included)</p>
                <p className="font-medium text-sm">{detail.bom.name || detail.bom.variant_code} <Badge className="bg-slate-900 ml-1 text-[10px]">primary</Badge></p>
              </div>
            )}
            <div className="border border-slate-200 rounded-sm">
              <div className="p-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <span className="font-semibold text-xs">Available Part-Assembly BOMs</span>
                <span className="text-xs text-slate-500" data-testid="link-boms-selected-count">{linkedDraft.length} selected</span>
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                {allBoms.length === 0 ? (
                  <p className="text-xs text-slate-500 p-3">No other BOMs available to link.</p>
                ) : allBoms.map(b => {
                  const checked = linkedDraft.includes(b.id);
                  return (
                    <label key={b.id} className="flex items-start gap-2 p-2 hover:bg-slate-50 cursor-pointer text-xs" data-testid={`link-bom-row-${b.id}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setLinkedDraft(prev => e.target.checked ? [...prev, b.id] : prev.filter(x => x !== b.id));
                        }}
                        className="mt-0.5"
                        data-testid={`link-bom-check-${b.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{b.bom_name || b.bom_variant_code}</p>
                        <p className="text-slate-500 font-mono text-[10px]">{b.bom_variant_code} · v{b.bom_revision || b.revision_number || '—'} · {b.status || ''}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkBomsOpen(false)}>Cancel</Button>
            <Button onClick={saveLinkedBoms} className="bg-slate-900 hover:bg-slate-800" data-testid="link-boms-save-btn">
              Save linked BOMs ({linkedDraft.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===== Subcomponents =====
const UnitGrid = ({ loading, items, ptMap, onOpen, onShip, onPrintLabel, archive, testidPrefix }) => {
  if (loading) return <div className="h-48 flex items-center justify-center"><div className="spinner" /></div>;
  if (items.length === 0) {
    return (
      <Card className="industrial-card">
        <CardContent className="py-12 flex flex-col items-center justify-center text-slate-500">
          <Tag className="w-12 h-12 mb-3 text-slate-300" />
          <p>No {archive ? 'shipped' : 'onsite'} units yet</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {items.map(u => <UnitCard key={u.id} unit={u} pt={ptMap[u.product_type_id]} onOpen={onOpen} onShip={onShip} onPrintLabel={onPrintLabel} archive={archive} testidPrefix={testidPrefix} />)}
    </div>
  );
};

const UnitCard = ({ unit, pt, onOpen, onShip, onPrintLabel, archive, testidPrefix }) => {
  const isFinal = unit.unit_kind === 'final_product';
  const detailUrl = `${FRONTEND_URL}/finished-units?serial=${unit.serial_number}`;
  return (
    <Card className="industrial-card hover:shadow-md transition-shadow" data-testid={`${testidPrefix}-unit-card-${unit.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 uppercase tracking-wide">{isFinal ? 'Final Product' : 'Part Assembly'}</p>
            <p className="font-semibold truncate">{pt?.product_type_name || '—'}</p>
            <p className="font-mono text-xs text-slate-700 mt-0.5 truncate">{unit.serial_number}</p>
          </div>
          {isFinal ? <PackageIcon className="w-5 h-5 text-orange-500" /> : <Network className="w-5 h-5 text-slate-500" />}
        </div>
        <div className="flex items-center justify-between bg-slate-50 rounded-sm p-2">
          <div className="bg-white p-1 rounded-sm border border-slate-200">
            <QRCodeSVG value={detailUrl} size={56} />
          </div>
          <div className="flex flex-col items-end gap-1 text-xs">
            <Badge variant="outline" className="font-mono">v{unit.bom_revision || '—'}</Badge>
            {isFinal && <Badge variant="outline">{unit.child_unit_ids?.length || 0} linked</Badge>}
            <Badge className={archive ? 'bg-slate-500' : 'bg-emerald-600'}>{archive ? 'shipped' : 'onsite'}</Badge>
          </div>
        </div>
        <div className="text-xs text-slate-500 space-y-0.5">
          <p>Built {unit.build_date ? new Date(unit.build_date).toLocaleDateString() : '—'}</p>
          <p>By {unit.built_by_name || '—'}</p>
          {archive && unit.shipped_to && <p>To <span className="font-medium">{unit.shipped_to}</span></p>}
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={() => onOpen(unit)} className="flex-1" data-testid={`${testidPrefix}-unit-open-${unit.id}`}>
            <Eye className="w-4 h-4 mr-1" /> Details
          </Button>
          {onPrintLabel && (
            <Button size="sm" variant="outline" onClick={() => onPrintLabel(unit)} className="text-slate-700" data-testid={`${testidPrefix}-unit-label-${unit.id}`} title="Print label (pick size)">
              <Tag className="w-4 h-4" />
            </Button>
          )}
          {!archive && onShip && (
            <Button size="sm" variant="outline" onClick={() => onShip(unit)} className="text-blue-600" data-testid={`${testidPrefix}-unit-ship-${unit.id}`} title="Ship">
              <Truck className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const UnitDetailBody = ({ detail, ptMap, noteBody, setNoteBody, addNote, deleteNote, currentUser, bols, onIssuePublicLink, onRevokePublicLink, onOpenLinkBoms }) => {
  const u = detail.unit;
  const isFinal = u.unit_kind === 'final_product';
  const lifecycle = u.lifecycle_state || (u.current_status === 'Shipped' ? 'shipped' : 'onsite');
  const detailUrl = `${FRONTEND_URL}/finished-units?serial=${u.serial_number}`;
  const publicLinkActive = !!u.public_token && !u.public_token_revoked;
  const publicUrl = publicLinkActive ? `${FRONTEND_URL}/public/finished-units/${u.public_token}` : '';
  const copyPublic = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Public URL copied');
    } catch { toast.error('Copy failed'); }
  };
  return (
    <div className="space-y-4" data-testid="unit-detail-body">
      {/* Header card */}
      <div className="flex items-start gap-4 bg-slate-50 rounded-sm p-3">
        <div className="bg-white rounded-sm p-2 border border-slate-200">
          <QRCodeSVG value={detailUrl} size={88} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={isFinal ? 'bg-orange-500' : 'bg-slate-500'}>{isFinal ? 'Final Product' : 'Part Assembly'}</Badge>
            <Badge className={lifecycle === 'onsite' ? 'bg-emerald-600' : 'bg-slate-500'}>{lifecycle}</Badge>
            <Badge variant="outline" className="font-mono">v{u.bom_revision || '—'}</Badge>
            {detail.totals.qc_pass > 0 && <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" />{detail.totals.qc_pass} QC pass</Badge>}
            {detail.totals.qc_fail > 0 && <Badge className="bg-red-600"><XCircle className="w-3 h-3 mr-1" />{detail.totals.qc_fail} QC fail</Badge>}
          </div>
          <p className="text-lg font-semibold mt-1">{detail.product_type?.product_type_name || '—'}</p>
          <p className="font-mono text-sm">{u.serial_number}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-xs">
            <div><p className="text-slate-500">Built</p><p>{u.build_date ? new Date(u.build_date).toLocaleString() : '—'}</p></div>
            <div><p className="text-slate-500">By</p><p>{u.built_by_name || '—'}</p></div>
            <div><p className="text-slate-500">BOM</p><p>{detail.bom?.variant_code || '—'}</p></div>
            <div><p className="text-slate-500">WO</p><p className="font-mono">{u.work_order_id ? `WO-${u.work_order_id.slice(0, 8).toUpperCase()}` : '—'}</p></div>
            {lifecycle === 'shipped' && (
              <>
                <div className="col-span-2"><p className="text-slate-500">Shipped to</p><p>{u.shipped_to || '—'}</p></div>
                <div className="col-span-2"><p className="text-slate-500">Tracking</p><p className="font-mono">{u.shipped_tracking || '—'}</p></div>
              </>
            )}
            {detail.parent && (
              <div className="col-span-2"><p className="text-slate-500">Linked to Final Product</p><p className="font-mono">{detail.parent.serial_number}</p></div>
            )}
          </div>
        </div>
      </div>

      {/* iter37 — Final Assembly Info Card (Final Products only): Public read-only link + Linked PA BOMs at a glance */}
      {isFinal && (
        <Card className="industrial-card border-orange-200" data-testid="final-info-card">
          <CardContent className="p-3 space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-sm flex items-center gap-1.5"><PackageIcon className="w-4 h-4 text-orange-600" /> Final Assembly Info Card</p>
              <Badge variant="outline" className="text-[10px]">{(detail.linked_boms || []).length} linked + 1 primary</Badge>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {/* Public Link panel */}
              <div className="border border-slate-200 rounded-sm p-3 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <Link2 className="w-3.5 h-3.5" /> Public Read-Only Link
                  </p>
                  {publicLinkActive ? (
                    <Badge className="bg-emerald-600 text-[10px]" data-testid="public-link-status">ACTIVE</Badge>
                  ) : (
                    <Badge className="bg-slate-400 text-[10px]" data-testid="public-link-status">NOT ISSUED</Badge>
                  )}
                </div>
                {publicLinkActive ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-center bg-slate-50 rounded-sm p-2 border border-slate-100">
                      <QRCodeSVG value={publicUrl} size={84} data-testid="public-link-qr" />
                    </div>
                    <div className="flex items-center gap-1">
                      <Input value={publicUrl} readOnly className="text-[10px] font-mono h-7" data-testid="public-link-url-input" />
                      <Button size="sm" variant="outline" onClick={copyPublic} className="h-7 px-2" data-testid="public-link-copy-btn">
                        <CopyIcon className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => window.open(publicUrl, '_blank')} className="h-7 px-2" data-testid="public-link-open-btn">
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    </div>
                    <Button size="sm" variant="outline" onClick={onRevokePublicLink} className="text-red-600 w-full h-7 text-xs" data-testid="public-link-revoke-inline-btn">
                      <Link2Off className="w-3 h-3 mr-1" /> Revoke link
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-[11px] text-slate-500">Issue a token-protected URL anyone can scan to view this Final Assembly card (BOM rollup, QC, recalls). The link can be revoked anytime; revoking instantly disables every printed QR for this unit.</p>
                    <Button size="sm" onClick={onIssuePublicLink} className="bg-orange-600 hover:bg-orange-700 w-full h-7 text-xs" data-testid="public-link-issue-inline-btn">
                      <Link2 className="w-3 h-3 mr-1" /> Issue public link
                    </Button>
                  </div>
                )}
              </div>
              {/* Linked BOMs panel */}
              <div className="border border-slate-200 rounded-sm p-3 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <Network className="w-3.5 h-3.5" /> BOM Roll-Up
                  </p>
                  <Button size="sm" variant="outline" onClick={onOpenLinkBoms} className="h-6 text-[11px]" data-testid="info-card-manage-boms-btn">
                    Manage
                  </Button>
                </div>
                <ul className="space-y-1.5 text-xs">
                  {detail.bom && (
                    <li className="flex items-center justify-between gap-2 py-0.5" data-testid="info-card-primary-bom">
                      <div className="min-w-0 flex-1">
                        <span className="font-mono text-[10px] text-slate-500">{detail.bom.variant_code}</span>
                        <p className="truncate">{detail.bom.name || '—'}</p>
                      </div>
                      <Badge className="bg-slate-900 text-[9px] shrink-0">primary v{detail.bom.revision || '—'}</Badge>
                    </li>
                  )}
                  {(detail.linked_boms || []).map(b => (
                    <li key={b.id} className="flex items-center justify-between gap-2 py-0.5 border-t border-slate-100 pt-1.5" data-testid={`info-card-linked-bom-${b.id}`}>
                      <div className="min-w-0 flex-1">
                        <span className="font-mono text-[10px] text-slate-500">{b.bom_variant_code || '—'}</span>
                        <p className="truncate">{b.bom_name || '—'}</p>
                      </div>
                      <Badge variant="outline" className="text-[9px] shrink-0">linked v{b.bom_revision || b.revision_number || '—'}</Badge>
                    </li>
                  ))}
                  {(detail.linked_boms || []).length === 0 && (
                    <li className="text-[11px] text-slate-400 italic py-0.5 border-t border-slate-100 pt-1.5">
                      No additional Part-Assembly BOMs linked yet. Click <span className="font-semibold">Manage</span> to add one.
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Linked PA serials (final products only) */}
      {isFinal && detail.linked_units.length > 0 && (
        <Card className="industrial-card" data-testid="unit-linked-units-card">
          <CardContent className="p-3">
            <p className="font-semibold mb-2 text-sm">Part-Assembly Serials ({detail.linked_units.length})</p>
            <Table className="data-table">
              <TableHeader><TableRow>
                <TableHead>Serial #</TableHead><TableHead>Product</TableHead><TableHead>Rev</TableHead><TableHead>Built</TableHead><TableHead>By</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {detail.linked_units.map(c => (
                  <TableRow key={c.id}>
                    <TableCell className="font-mono text-xs">{c.serial_number}</TableCell>
                    <TableCell className="text-xs">{c.product_type || '—'}</TableCell>
                    <TableCell className="text-xs">v{c.bom_revision || '—'}</TableCell>
                    <TableCell className="text-xs">{c.build_date ? new Date(c.build_date).toLocaleDateString() : '—'}</TableCell>
                    <TableCell className="text-xs">{c.built_by_name || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Component SKUs from BOM */}
      <Card className="industrial-card" data-testid="unit-components-card">
        <CardContent className="p-3">
          <p className="font-semibold mb-2 text-sm">Component SKUs ({detail.components.length})</p>
          {detail.components.length === 0 ? (
            <p className="text-xs text-slate-500">BOM has no components.</p>
          ) : (
            <Table className="data-table">
              <TableHeader><TableRow>
                <TableHead>SKU</TableHead><TableHead>Item</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Qty/Unit</TableHead><TableHead>UoM</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {detail.components.map((c, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono text-xs">{c.component_sku}</TableCell>
                    <TableCell className="text-xs">{c.component_name || '—'}</TableCell>
                    <TableCell className="text-xs">{c.category || '—'}</TableCell>
                    <TableCell className="text-right text-xs">{c.quantity_per_unit ?? '—'}</TableCell>
                    <TableCell className="text-xs">{c.uom || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Bills of Lading tied to this unit */}
      <Card className="industrial-card" data-testid="unit-bols-card">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-sm">Bills of Lading ({(bols || []).length})</p>
            <a href="/shipping" className="text-xs text-orange-600 underline" data-testid="unit-shipping-link">Open Shipping →</a>
          </div>
          {(bols || []).length === 0 ? (
            <p className="text-xs text-slate-500">No BOLs tied to this unit yet. Use the Shipping page to draft and issue one.</p>
          ) : (
            <Table className="data-table">
              <TableHeader><TableRow>
                <TableHead>BOL #</TableHead><TableHead>Status</TableHead><TableHead>Consignee</TableHead><TableHead>Carrier</TableHead><TableHead>Issued</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(bols || []).map(b => (
                  <TableRow key={b.id} data-testid={`unit-bol-row-${b.id}`}>
                    <TableCell className="font-mono">{b.bol_number || <span className="text-slate-400 italic">draft</span>}</TableCell>
                    <TableCell><Badge className={b.status === 'Issued' ? 'bg-emerald-600' : b.status === 'Voided' ? 'bg-red-600' : 'bg-slate-500'}>{b.status}</Badge></TableCell>
                    <TableCell className="text-xs">{b.consignee_name || '—'}</TableCell>
                    <TableCell className="text-xs">{b.carrier || '—'}</TableCell>
                    <TableCell className="text-xs">{b.issued_at ? new Date(b.issued_at).toLocaleDateString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="industrial-card" data-testid="unit-repair-history-card">
        <CardContent className="p-3">
          <p className="font-semibold mb-2 text-sm">Repair History ({(detail.repair_history || []).length})</p>
          {!(detail.repair_history || []).length ? <p className="text-xs text-slate-400">No repair orders for this serial.</p> : (
            <div className="space-y-1">
              {detail.repair_history.map(ro => (
                <Link key={ro.id} to={`/orders/repairs/${ro.id}`} className="flex items-center justify-between text-xs border border-slate-200 rounded-sm px-2 py-1.5 hover:bg-slate-50" data-testid={`unit-repair-${ro.id}`}>
                  <span><strong className="mono">{ro.ro_number}</strong> · {ro.intake_date} · {(ro.reported_issue || '').slice(0, 60)}</span>
                  <span className="uppercase text-[10px] bg-slate-100 px-1.5 py-0.5 rounded-sm">{ro.status}</span>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* QC submissions */}
      <Card className="industrial-card" data-testid="unit-qc-card">
        <CardContent className="p-3">
          <p className="font-semibold mb-2 text-sm">QC Forms ({detail.qc_submissions.length})</p>
          {detail.qc_submissions.length === 0 ? (
            <p className="text-xs text-slate-500">No QC submissions tied to this unit.</p>
          ) : (
            <Table className="data-table">
              <TableHeader><TableRow>
                <TableHead>Template</TableHead><TableHead>Result</TableHead><TableHead>Inspector</TableHead><TableHead>Date</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {detail.qc_submissions.map(q => (
                  <TableRow key={q.id} data-testid={`unit-qc-row-${q.id}`}>
                    <TableCell className="text-xs">{q.template_name}</TableCell>
                    <TableCell><Badge className={q.overall_status === 'pass' ? 'bg-emerald-600' : q.overall_status === 'fail' ? 'bg-red-600' : 'bg-slate-400'}>{q.overall_status?.toUpperCase()}</Badge></TableCell>
                    <TableCell className="text-xs">{q.submitted_by_name || '—'}</TableCell>
                    <TableCell className="text-xs">{q.submitted_at ? new Date(q.submitted_at).toLocaleDateString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      <Card className="industrial-card" data-testid="unit-notes-card">
        <CardContent className="p-3 space-y-2">
          <p className="font-semibold text-sm">Notes ({detail.notes.length})</p>
          <div className="flex gap-2">
            <Textarea value={noteBody} onChange={e => setNoteBody(e.target.value)} placeholder="Add a note…" rows={2} data-testid="unit-note-input" />
            <Button onClick={addNote} disabled={!noteBody.trim()} className="self-start" data-testid="unit-note-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
          {detail.notes.length === 0 ? (
            <p className="text-xs text-slate-500">No notes yet.</p>
          ) : (
            <ul className="space-y-1">
              {detail.notes.map(n => (
                <li key={n.id} className="border border-slate-200 rounded-sm p-2 text-sm flex items-start justify-between gap-2" data-testid={`unit-note-row-${n.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="font-medium text-slate-700">{n.author_name || '—'}</span>
                      <span>·</span>
                      <span>{n.created_at ? new Date(n.created_at).toLocaleString() : '—'}</span>
                      {n.kind && n.kind !== 'note' && <Badge variant="outline" className="ml-1 text-[10px]">{n.kind}</Badge>}
                    </div>
                    <p className="whitespace-pre-wrap mt-1">{n.body}</p>
                  </div>
                  {(n.author_id === currentUser?.id || currentUser?.role === 'Admin') && (
                    <Button size="sm" variant="ghost" onClick={() => deleteNote(n.id)} className="text-red-600" data-testid={`unit-note-delete-${n.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ====== Print sheet ======
const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const renderUnitSheetHtml = (data) => {
  const u = data.unit;
  const compRows = (data.components || []).map(c => `<tr><td>${escapeHtml(c.component_sku)}</td><td>${escapeHtml(c.component_name)}</td><td>${escapeHtml(c.category)}</td><td style="text-align:right">${escapeHtml(c.quantity_per_unit)}</td><td>${escapeHtml(c.uom)}</td></tr>`).join('');
  const linkRows = (data.linked_units || []).map(c => `<tr><td>${escapeHtml(c.serial_number)}</td><td>${escapeHtml(c.product_type)}</td><td>v${escapeHtml(c.bom_revision)}</td><td>${c.build_date ? new Date(c.build_date).toLocaleDateString() : '—'}</td><td>${escapeHtml(c.built_by_name)}</td></tr>`).join('');
  const qcRows = (data.qc_submissions || []).map(q => `<tr><td>${escapeHtml(q.template_name)}</td><td>${(q.overall_status || '').toUpperCase()}</td><td>${escapeHtml(q.submitted_by_name)}</td><td>${q.submitted_at ? new Date(q.submitted_at).toLocaleDateString() : ''}</td></tr>`).join('');
  const noteRows = (data.notes || []).map(n => `<li><strong>${escapeHtml(n.author_name)}</strong> · ${n.created_at ? new Date(n.created_at).toLocaleString() : ''}<br/>${escapeHtml(n.body)}</li>`).join('');
  return `<!doctype html><html><head><title>Unit Sheet ${escapeHtml(u.serial_number)}</title>
  <style>
    @page { size: letter portrait; margin: 0.5in; }
    body { font-family: -apple-system, system-ui, Arial, sans-serif; color:#0f172a; font-size:12px; }
    h1 { font-size: 22px; margin: 0; }
    h2 { font-size: 14px; margin: 12px 0 4px; border-bottom:1px solid #cbd5e1; padding-bottom:2px; }
    .meta { display:grid; grid-template-columns:repeat(3, 1fr); gap:6px; margin: 8px 0; }
    .cell { background:#f1f5f9; padding:6px 8px; border-radius:2px; }
    .label { color:#64748b; font-size:10px; }
    .value { font-weight:600; }
    table { width:100%; border-collapse:collapse; margin-top:4px; }
    th, td { padding:4px 6px; border-bottom:1px solid #cbd5e1; text-align:left; }
    th { background:#e2e8f0; }
    ul { padding-left: 18px; }
  </style></head><body>
  <!--LETTERHEAD-->
  <div class="meta">
    <div class="cell"><div class="label">Product</div><div class="value">${escapeHtml(data.product_type?.product_type_name)}</div></div>
    <div class="cell"><div class="label">Type</div><div class="value">${u.unit_kind === 'final_product' ? 'Final Product' : 'Part Assembly'}</div></div>
    <div class="cell"><div class="label">Lifecycle</div><div class="value">${u.lifecycle_state || (u.current_status === 'Shipped' ? 'shipped' : 'onsite')}</div></div>
    <div class="cell"><div class="label">BOM</div><div class="value">${escapeHtml(data.bom?.variant_code)} v${escapeHtml(u.bom_revision)}</div></div>
    <div class="cell"><div class="label">Built</div><div class="value">${u.build_date ? new Date(u.build_date).toLocaleString() : '—'}</div></div>
    <div class="cell"><div class="label">Built By</div><div class="value">${escapeHtml(u.built_by_name)}</div></div>
    ${u.lifecycle_state === 'shipped' ? `<div class="cell"><div class="label">Shipped To</div><div class="value">${escapeHtml(u.shipped_to)}</div></div><div class="cell"><div class="label">Tracking</div><div class="value">${escapeHtml(u.shipped_tracking)}</div></div><div class="cell"><div class="label">Shipped At</div><div class="value">${u.shipped_at ? new Date(u.shipped_at).toLocaleString() : ''}</div></div>` : ''}
  </div>
  ${linkRows ? `<h2>Part-Assembly Serials</h2><table><tr><th>Serial</th><th>Product</th><th>Rev</th><th>Built</th><th>By</th></tr>${linkRows}</table>` : ''}
  <h2>Components (${(data.components || []).length})</h2>
  <table><tr><th>SKU</th><th>Item</th><th>Category</th><th style="text-align:right">Qty/Unit</th><th>UoM</th></tr>${compRows || '<tr><td colspan="5">No components.</td></tr>'}</table>
  ${qcRows ? `<h2>QC Submissions</h2><table><tr><th>Template</th><th>Result</th><th>Inspector</th><th>Date</th></tr>${qcRows}</table>` : ''}
  ${noteRows ? `<h2>Notes</h2><ul>${noteRows}</ul>` : ''}
  </body></html>`;
};

export default FinishedUnitsPage;
